import { useEffect, useRef } from "react";
import styled from "styled-components";
import FormContainer from "../../../common/forms/FormContainer";
import FormField from "../../../common/forms/FormField";
import SimpleLabelField from "../../../common/forms/SimpleLabelField";

// #region SCOPED CSS
const FormStyles = styled.div`
    .info-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
    }

    .info-grid-2col {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
    }

    .info-grid-right {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .info-grid-right-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
    }

    .span-full {
        grid-column: span 1;
    }

    .section-gap {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    /*.Frame48.locked {
        border-left: 5px solid var(--backgrounds-lines-n-500, #141922);
    }*/

    .frame-53 {
        padding: 0px 5px 0px 5px;
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: center;
        justify-content: flex-end;
        align-self: stretch;
        flex-shrink: 0;
        position: relative;
    }
    .plus-square {
        flex-shrink: 0;
        width: 9px;
        height: 9px;
        position: relative;
        overflow: visible;
        aspect-ratio: 1;
    }

    .muted .Frame51 .Title,
    .muted .Frame51 svg {
        color: var(--text-icons-gray-300) !important;
    }

    .info-available-container.form-muted .card-title {
        color: var(--text-icons-gray-300) !important;
    }
`;

const TooltipTitle = styled.div`
    color: var(--text-icons-gray-100, #d1d1d1);
    font-size: 12px;
    font-weight: 700;
    text-align: left;
`;

const TooltipBody = styled.div`
    color: var(--text-icons-gray-100, #d1d1d1);
    font-size: 12px;
    font-weight: 400;
    text-align: left;
`;
// #endregion

export default function POSExpInfoAvailableCard({
    posInfoData = {},
    formsEnabled = false,
    descriptionsIncluded,
    setDescriptionsIncluded,
    setSaveButtonEnabled
}) {
    // #region VARS
    const containerRef = useRef(null);
    // #endregion

    // #region METHODS
    const onAddToDescription = fieldName => {
        setDescriptionsIncluded(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleClick = e => {
            const btn = e.target.closest("[data-desc-field]");
            if (!btn || !formsEnabled) return;
            const fieldName = btn.getAttribute("data-desc-field");
            if (fieldName) onAddToDescription(fieldName);
            setSaveButtonEnabled(true);
        };

        container.addEventListener("click", handleClick);
        return () => container.removeEventListener("click", handleClick);
    });
    // #endregion

    // #region RENDER
    return (
        <FormStyles ref={containerRef} className="w-100">
            <FormContainer
                className={`w-100 info-available-container ${!formsEnabled ? "form-muted" : ""}`}
                title="INFO AVAILABLE">
                <div className="section-gap">
                    {/* ROW 1: Item nr. | Item name | Season | Collection */}
                    <div className="info-grid">
                        <FormField
                            className={!formsEnabled ? "muted" : ""}
                            type="text"
                            title="Product number"
                            varName="productNumber"
                            value={posInfoData.productNumber}
                            assistiveText="INCLUDED"
                            assistiveTextColorOverride="var(--text-icons-gray-300)"
                            formFieldColumSize={1}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Product number" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                        <FormField
                            className={!formsEnabled ? "muted" : ""}
                            type="text"
                            title="Name"
                            varName="name"
                            value={posInfoData.name}
                            assistiveText="INCLUDED"
                            assistiveTextColorOverride="var(--text-icons-gray-300)"
                            formFieldColumSize={1}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Name" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                        <FormField
                            className={!formsEnabled ? "muted" : ""}
                            type="text"
                            title="Season"
                            varName="season"
                            value={posInfoData.season}
                            assistiveText="INCLUDED"
                            assistiveTextColorOverride="var(--text-icons-gray-300)"
                            formFieldColumSize={1}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Season" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                        <FormField
                            className={!formsEnabled ? "muted" : ""}
                            type="text"
                            title="Collections"
                            varName="collections"
                            value={posInfoData.collections}
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                            assistiveText="INCLUDED"
                            assistiveTextColorOverride="var(--text-icons-gray-300)"
                            formFieldColumSize={1}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Collections" })}
                            tooltipMaxWidth="400px"
                        />
                    </div>

                    {/* ROW 2: Description (left half) | Item type + Gender + Category (right half) */}
                    <div className="info-grid-2col">
                        <SimpleLabelField
                            className={!formsEnabled ? "muted hover-not-allowed" : "hover-not-allowed"}
                            title="Description"
                            varName="description"
                            value={posInfoData.description}
                            assistiveText="INCLUDED"
                            assistiveTextColorOverride="var(--text-icons-gray-300)"
                            formFieldColumSize={1}
                            styling={{
                                formFieldHeight: "118px"
                            }}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Description" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                        <div className="info-grid-right">
                            <div className="info-grid-right-row">
                                <FormField
                                    className={!formsEnabled ? "muted" : ""}
                                    type="text"
                                    title="Product group"
                                    varName="productGroup"
                                    value={posInfoData.productGroup}
                                    assistiveText="INCLUDED"
                                    assistiveTextColorOverride="var(--text-icons-gray-300)"
                                    formFieldColumSize={1}
                                    requiredType={"disabledSimple"}
                                    tooltip={InfoCardTooltipText({ fieldName: "Product group" })}
                                    tooltipMaxWidth="400px"
                                    placeholder={formsEnabled ? "Not provided by the brand" : ""}
                                />
                                <FormField
                                    className={!formsEnabled ? "muted" : ""}
                                    type="text"
                                    title="Gender"
                                    varName="gender"
                                    value={posInfoData.gender}
                                    assistiveText="INCLUDED"
                                    assistiveTextColorOverride="var(--text-icons-gray-300)"
                                    formFieldColumSize={1}
                                    requiredType={"disabledSimple"}
                                    tooltip={InfoCardTooltipText({ fieldName: "Gender" })}
                                    tooltipMaxWidth="400px"
                                    placeholder={formsEnabled ? "Not provided by the brand" : ""}
                                />
                            </div>
                            <FormField
                                className={!formsEnabled ? "muted" : ""}
                                type="text"
                                title="Category"
                                varName="category"
                                value={posInfoData.category}
                                placeholder={formsEnabled ? "Not provided by the brand" : ""}
                                assistiveText={AddToDescriptionTextBtn({
                                    fieldName: "includeCategory",
                                    isAdd: !descriptionsIncluded?.includeCategory,
                                    formsEnabled
                                })}
                                assistiveTextColorOverride={
                                    descriptionsIncluded?.includeCategory
                                        ? "var(--text-icons-orange-500)"
                                        : "var(--text-icons-blue-500)"
                                }
                                formFieldColumSize={1}
                                requiredType={"disabledSimple"}
                                tooltip={InfoCardTooltipText({ fieldName: "Category" })}
                                tooltipMaxWidth="400px"
                            />
                        </div>
                    </div>

                    {/* ROW 3: Specifications | Composition | Wash & Care | Features */}
                    <div className="info-grid">
                        <SimpleLabelField
                            className={!formsEnabled ? "muted hover-not-allowed" : "hover-not-allowed"}
                            title="Specifications"
                            varName="specifications"
                            value={posInfoData.specifications}
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                            assistiveText={AddToDescriptionTextBtn({
                                fieldName: "includeSpecifications",
                                isAdd: !descriptionsIncluded?.includeSpecifications,
                                formsEnabled
                            })}
                            assistiveTextColorOverride={
                                descriptionsIncluded?.includeSpecifications
                                    ? "var(--text-icons-orange-500)"
                                    : "var(--text-icons-blue-500)"
                            }
                            formFieldColumSize={1}
                            styling={{
                                formFieldHeight: "118px"
                            }}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Specifications" })}
                            tooltipMaxWidth="400px"
                        />
                        <SimpleLabelField
                            className={!formsEnabled ? "muted hover-not-allowed" : "hover-not-allowed"}
                            title="Composition"
                            varName="composition"
                            value={posInfoData.composition}
                            assistiveText={AddToDescriptionTextBtn({
                                fieldName: "includeComposition",
                                isAdd: !descriptionsIncluded?.includeComposition,
                                formsEnabled
                            })}
                            assistiveTextColorOverride={
                                descriptionsIncluded?.includeComposition
                                    ? "var(--text-icons-orange-500)"
                                    : "var(--text-icons-blue-500)"
                            }
                            formFieldColumSize={1}
                            styling={{
                                formFieldHeight: "118px"
                            }}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Composition" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                        <SimpleLabelField
                            className={!formsEnabled ? "muted hover-not-allowed" : "hover-not-allowed"}
                            title="Wash & Care"
                            varName="washCare"
                            value={posInfoData.washCare}
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                            assistiveText={AddToDescriptionTextBtn({
                                fieldName: "includeWashAndCare",
                                isAdd: !descriptionsIncluded?.includeWashAndCare,
                                formsEnabled
                            })}
                            assistiveTextColorOverride={
                                descriptionsIncluded?.includeWashAndCare
                                    ? "var(--text-icons-orange-500)"
                                    : "var(--text-icons-blue-500)"
                            }
                            formFieldColumSize={1}
                            styling={{
                                formFieldHeight: "118px"
                            }}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Wash & Care" })}
                            tooltipMaxWidth="400px"
                        />
                        <SimpleLabelField
                            className={!formsEnabled ? "muted hover-not-allowed" : "hover-not-allowed"}
                            title="Features"
                            varName="features"
                            value={posInfoData.features}
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                            assistiveText={AddToDescriptionTextBtn({
                                fieldName: "includeFeatures",
                                isAdd: !descriptionsIncluded?.includeFeatures,
                                formsEnabled
                            })}
                            assistiveTextColorOverride={
                                descriptionsIncluded?.includeFeatures
                                    ? "var(--text-icons-orange-500)"
                                    : "var(--text-icons-blue-500)"
                            }
                            formFieldColumSize={1}
                            styling={{
                                formFieldHeight: "118px"
                            }}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Features" })}
                            tooltipMaxWidth="400px"
                        />
                    </div>

                    {/* ROW 4: Quality description | Country of origin | Price IN | Price OUT */}
                    <div className="info-grid">
                        <FormField
                            className={!formsEnabled ? "muted" : ""}
                            type="text"
                            title="Quality description"
                            varName="qualityDescription"
                            value={posInfoData.qualityDescription}
                            assistiveText={AddToDescriptionTextBtn({
                                fieldName: "includeQualityDescription",
                                isAdd: !descriptionsIncluded?.includeQualityDescription,
                                formsEnabled
                            })}
                            assistiveTextColorOverride={
                                descriptionsIncluded?.includeQualityDescription
                                    ? "var(--text-icons-orange-500)"
                                    : "var(--text-icons-blue-500)"
                            }
                            formFieldColumSize={1}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Quality description" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                        <FormField
                            className={!formsEnabled ? "muted" : ""}
                            type="text"
                            title="Country of origin"
                            varName="countryOfOrigin"
                            value={posInfoData.countryOfOrigin}
                            assistiveText={AddToDescriptionTextBtn({
                                fieldName: "includeCountryOfOrigin",
                                isAdd: !descriptionsIncluded?.includeCountryOfOrigin,
                                formsEnabled
                            })}
                            assistiveTextColorOverride={
                                descriptionsIncluded?.includeCountryOfOrigin
                                    ? "var(--text-icons-orange-500)"
                                    : "var(--text-icons-blue-500)"
                            }
                            formFieldColumSize={1}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Country of origin" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                        <FormField
                            className={!formsEnabled ? "muted" : ""}
                            type="text"
                            title="Cost current"
                            varName="costCurrent"
                            value={posInfoData.costCurrent}
                            assistiveText="INCLUDED"
                            assistiveTextColorOverride="var(--text-icons-gray-300)"
                            formFieldColumSize={1}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Cost current" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                        <FormField
                            className={!formsEnabled ? "muted" : ""}
                            type="text"
                            title="Sale price"
                            varName="salePrice"
                            value={posInfoData.salePrice}
                            assistiveText="INCLUDED"
                            assistiveTextColorOverride="var(--text-icons-gray-300)"
                            formFieldColumSize={1}
                            requiredType={"disabledSimple"}
                            tooltip={InfoCardTooltipText({ fieldName: "Sale price" })}
                            tooltipMaxWidth="400px"
                            placeholder={formsEnabled ? "Not provided by the brand" : ""}
                        />
                    </div>
                </div>
            </FormContainer>
        </FormStyles>
    );
    // #endregion
}

// #region SMALL COMPONENTS
const AddToDescriptionTextBtn = ({ fieldName, isAdd = true, formsEnabled }) => {
    // eslint-disable-next-line quotes
    const PLUS_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;
    // eslint-disable-next-line quotes
    const MINUS_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;

    const labelText = isAdd ? "ADD TO DESCRIPTION" : "REMOVE FROM DESCRIPTION";
    const iconSvg = isAdd ? PLUS_SVG : MINUS_SVG;
    const className = formsEnabled ? "cursor-pointer" : "text-inactive hover-not-allowed";

    return `<div class="frame-53 ${className}" data-desc-field="${fieldName}">
            ${iconSvg}
            <div class="add-to-description ${className}">${labelText}</div>
        </div>`;
};

const InfoCardTooltipText = ({ fieldName }) => {
    return (
        <div style={{ padding: "0 4px" }}>
            <TooltipTitle>{fieldName}</TooltipTitle>

            <TooltipBody style={{ marginTop: "4px" }}>
                This information is available from the product master.
                <br />
                “Included” means this field is already part of the Front Systems export.
                <br />
                “Add to description” adds the full field content to the Front Systems description text box under the
                product image.
                <br />
                “Remove from description” removes this field from that description text box.
                <br />
                When added, the field is shown with a separator line and the field name. If translation is selected,
                this text will also be translated.
                <br />
                This does not change the original BOtag product data.
            </TooltipBody>
        </div>
    );
};

// #endregion
